/**
 * Hero / Banner para la página principal.
 * Collage asimétrico (una imagen grande + varias pequeñas), overlay con gradiente,
 * celdas rellenadas con módulo (sin huecos grises), transiciones suaves.
 */
const Hero = ({
  imageUrls = [],
  title = 'Illustra',
  subtitle = 'Donde el arte se encuentra',
  extraText = 'Inspira. Crea. Comparte.',
}) => {
  const hasImages = imageUrls && imageUrls.length > 0;
  const len = imageUrls.length;
  // 5 celdas: 1 grande (2x2) + 4 pequeñas (2x2 a la derecha)
  const cellCount = 5;
  const cells = Array.from({ length: cellCount }, (_, i) => imageUrls[i % len] || null);

  return (
    <section
      className="relative w-[95%] max-w-[95%] mx-auto rounded-2xl overflow-hidden min-h-[26vh] sm:min-h-[30vh] md:min-h-[34vh] flex items-center justify-start shadow-xl"
      aria-label="Banner principal"
    >
      {/* Fondo: collage asimétrico */}
      {hasImages ? (
        <div
          className="absolute inset-0 grid grid-cols-4 grid-rows-2 gap-0.5"
          style={{ gridTemplateAreas: '"big big right1 right2" "big big right3 right4"' }}
          aria-hidden
        >
          {cells.map((url, i) => {
            if (!url) return null;
            const isBig = i === 0;
            const area = isBig ? 'big' : `right${i}`;
            return (
              <div
                key={`${url}-${i}`}
                className="bg-cover bg-center bg-no-repeat opacity-95 transition-opacity duration-300 rounded-sm ring-1 ring-white/5"
                style={{
                  backgroundImage: `url(${url})`,
                  gridArea: area,
                }}
              />
            );
          })}
        </div>
      ) : (
        <div
          className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500"
          aria-hidden
        />
      )}

      {/* Overlay unificado: poco blur + gradiente para legibilidad */}
      <div
        className="absolute inset-0 backdrop-blur-[1px] bg-black/25"
        aria-hidden
      />
      <div
        className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/15 to-transparent pointer-events-none"
        aria-hidden
      />

      {/* Borde sutil */}
      <div
        className="absolute inset-0 rounded-2xl pointer-events-none ring-1 ring-white/10"
        aria-hidden
      />

      {/* Contenido: alineado a la izquierda, tipografía distintiva */}
      <div className="relative z-10 text-left pl-6 pr-4 py-6 sm:pl-10 sm:pr-8 sm:py-8 md:pl-14 md:pr-12 md:py-10 max-w-2xl">
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white tracking-tight drop-shadow-xl [text-shadow:0_2px_20px_rgba(0,0,0,0.4)]">
          {title}
        </h1>
        <p className="mt-2 sm:mt-3 font-sans font-light text-lg sm:text-xl text-white/95 drop-shadow-md max-w-md">
          {subtitle}
        </p>
        {extraText && (
          <p className="mt-3 sm:mt-4 uppercase tracking-[0.2em] text-xs sm:text-sm font-semibold text-white/80 drop-shadow">
            {extraText}
          </p>
        )}
      </div>
    </section>
  );
};

export default Hero;
